import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import MDBox from 'components/MDBox';
import MDBadge from 'components/MDBadge';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { formatDate } from 'lib/utils';
import DataTable from 'components/Tables/DataTable';
import { UserIdentified } from 'components/UserIdentified';

import { AGGIORNA, RIMUOVI } from 'context/constants';
import { ADMIN, EDITOR } from '../context/auth';

function UserDataTable({ getData, onEditClb, onDeleteClb, reupdate = 'initial', onupdate }) {
  const [data, setData] = useState({ rows: [], columns: [], fetching: true });

  const sortUserFn = useMemo(() => {
    return (rowA, rowB) => {
      //, columnId, desc
      const nameA = `${rowA.values.element.props.surname} ${rowA.values.element.props.name}`;
      const nameB = `${rowB.values.element.props.surname} ${rowB.values.element.props.name}`;

      return nameA.localeCompare(nameB);
    };
  }, []);

  const sortEmailFn = useMemo(() => {
    return (rowA, rowB) => {
      const nameA = `${rowA.values.email.props.children}`;
      const nameB = `${rowB.values.email.props.children}`;

      return nameA.localeCompare(nameB);
    };
  }, []);

  const sortTypeFn = useMemo(() => {
    return (rowA, rowB) => {
      if (rowA?.values.type.props.children.props?.badgeContent.toLowerCase() === 'superadmin') {
        return -1;
      }
      if (rowB?.values.type.props.children.props?.badgeContent.toLowerCase() === 'superadmin') {
        return 1;
      }
      if (
        rowA?.values.type.props.children.props?.badgeContent.toLowerCase() === 'admin' &&
        rowB?.values.type.props.children.props?.badgeContent.toLowerCase() === 'viewer'
      ) {
        return -1;
      }
      if (
        rowB?.values.type.props.children.props?.badgeContent.toLowerCase() === 'admin' &&
        rowA?.values.type.props.children.props?.badgeContent.toLowerCase() === 'viewer'
      ) {
        return 1;
      }

      return 0;
    };
  }, []);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Utente',
        accessor: 'element',
        width: '45%',
        align: 'left',
        sortUndefined: 'last',
        sortType: sortUserFn,
      },
      {
        Header: 'Email',
        accessor: 'email',
        align: 'center',
        sortUndefined: 'last',
        sortType: sortEmailFn,
      },
      {
        Header: 'Tipo',
        accessor: 'type',
        align: 'center',
        sortUndefined: 'last',
        sortType: sortTypeFn,
      },
      { Header: 'Registrato il', accessor: 'inserted', align: 'center', disableSortBy: true },
      { Header: 'Action', accessor: 'action', align: 'center', disableSortBy: true },
    ];
  }, [sortUserFn, sortEmailFn, sortTypeFn]);

  /*const columns = [
      { Header: 'Utente', accessor: 'element', width: '45%', align: 'left' },
      { Header: 'Email', accessor: 'email', align: 'center' },
      { Header: 'Tipo', accessor: 'type', align: 'center' },
      { Header: 'Registrato il', accessor: 'inserted', align: 'center' },
      { Header: 'Action', accessor: 'action', align: 'center' },
    ];*/

  const onEdit = (event) => {
    event.stopPropagation();
    let { id } = event.target.dataset;
    if (!id) {
      //svg
      id = event.target.parentElement.dataset.id;
    }
    if (!id) {
      //path svg
      id = event.target.parentElement.parentElement.dataset.id;
    }

    !!onEditClb && 'function' === typeof onEditClb && onEditClb(id, AGGIORNA);
    //console.log('EDITO ELEMENTO:', id);
  };
  const onDelete = (event) => {
    event.stopPropagation();
    let { id } = event.target.dataset;
    if (!id) {
      //svg
      id = event.target.parentElement.dataset.id;
    }
    if (!id) {
      //path svg
      id = event.target.parentElement.parentElement.dataset.id;
    }

    !!onDeleteClb && 'function' === typeof onDeleteClb && onDeleteClb(id, RIMUOVI);
    //console.log('RIMUOVO ELEMENTO:', id);
  };

  const decorateFrontend = (dbData) => {
    const toFe = dbData.map((el) => {
      return {
        element: <UserIdentified id={el.id} name={el.nome} surname={el.cognome} />,
        email: (
          <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
            {el.email}
          </MDTypography>
        ),
        type: (
          <MDBox ml={-1}>
            <MDBadge
              badgeContent={
                +el.type === ADMIN ? 'admin' : +el.type === EDITOR ? 'editor' : 'viewer'
              }
              color={+el.type === ADMIN ? 'success' : +el.type === EDITOR ? 'warning' : 'error'}
              variant="gradient"
              size="sm"
            />
          </MDBox>
        ),
        inserted: (
          <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
            {formatDate(el.registered)}
          </MDTypography>
        ),
        action: (
          <>
            <MDButton
              sx={{ m: 1 }}
              variant="gradient"
              color="warning"
              onClick={onEdit}
              data-id={el.id}
            >
              <AppRegistrationIcon />
            </MDButton>
            <MDButton
              sx={{ m: 1 }}
              variant="gradient"
              color="error"
              onClick={onDelete}
              data-id={el.id}
            >
              <DeleteForeverIcon />
            </MDButton>
          </>
        ),
      };
    });

    return toFe;
  };

  useEffect(() => {
    try {
      if (reupdate !== '') {
        getData().then((reqData) => {
          !!onupdate && typeof onupdate === 'function' && onupdate(reqData);
          const mapped = decorateFrontend(reqData);
          setData({
            columns,
            rows: mapped,
            fetching: false,
          });
        });
      }
    } catch (e) {
      console.error(`getData: error on call: ${e.message()}`);
    }
    return () => console.log('UserDataTable unmounting...');
  }, [reupdate]);

  return (
    <DataTable
      table={{ columns: data.columns, rows: data.rows }}
      isSorted
      canSearch={false}
      entriesPerPage={{ defaultValue: 20, entries: [5, 10, 20, 50, 100] }}
      showTotalEntries
      noEndBorder
      loading={data.rows.length <= 0 && !!data.fetching}
    />
  );
}

UserDataTable.defaultProps = {
  getData: () => {},
  onEditClb: () => {},
  onDeleteClb: () => {},
  reupdate: 'initial',
  onupdate: () => {},
};

UserDataTable.propTypes = {
  getData: PropTypes.func,
  onEditClb: PropTypes.func,
  onDeleteClb: PropTypes.func,
  reupdate: PropTypes.string,
  onupdate: PropTypes.func,
};

export default UserDataTable;
